import {
  Action,
  configureStore,
  createSelector,
  Middleware,
  ThunkAction,
} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import apiService from 'app/store/apiService';
import {FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE} from 'redux-persist';
import {persistReducer, persistStore} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import {addKidApi} from '@/dataSource/src/api/add-kid/api';
import {cardsApi} from '@/dataSource/src/api/cards/api';
import {cardsThemeApi} from '@/dataSource/src/api/cards-theme/api';
import {editInfoApi} from '@/dataSource/src/api/editInfo/api';
import {splitBillsGroupsApi} from '@/dataSource/src/api/groups/api';
import {kycApi} from '@/dataSource/src/api/kyc/api';
import {paymentLinkApi} from '@/dataSource/src/api/payment-link/api';
import {paymentLinkV2Api} from '@/dataSource/src/api/payment-link/v2/api';
import {splitBillsApi} from '@/dataSource/src/api/split-bills/api';
import {splitsApi} from '@/dataSource/src/api/splits/api';
import {usersApi} from '@/dataSource/src/api/users/api';

import {rootReducer} from './lazyLoadedSlices';
import {loaderMiddleware} from './loaderMiddleware';
import {dynamicMiddleware} from './middleware';
import {userMiddleware} from './userMiddleware';

// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

const persistConfig = {
  key: 'root',
  storage,
  debug: true, // Enable logs for debugging
  blacklist: ['fuseDialog'], // Exclude keys not desired.
};

const middlewares: Middleware[] = [
  apiService.middleware,
  dynamicMiddleware,
  usersApi.middleware,
  userMiddleware,
  kycApi.middleware,
  cardsApi.middleware,
  addKidApi.middleware,
  cardsThemeApi.middleware,
  paymentLinkApi.middleware,
  paymentLinkV2Api.middleware,
  splitBillsApi.middleware,
  splitsApi.middleware,
  splitBillsGroupsApi.middleware,
  editInfoApi.middleware,
  loaderMiddleware,
];

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const makeStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            FLUSH,
            REHYDRATE,
            PAUSE,
            PERSIST,
            PURGE,
            REGISTER,
            'fuseDialog/openDialog',
          ],
        },
      }).concat(middlewares),
    preloadedState,
  });
  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  setupListeners(store.dispatch);
  return store;
};

export const store = makeStore();

// Infer the type of `store`
export type AppStore = typeof store;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ThunkReturnType = void> = ThunkAction<
  ThunkReturnType,
  RootState,
  unknown,
  Action
>;
export type AppAction<R = Promise<void>> =
  | Action<string>
  | ThunkAction<R, RootState, unknown, Action<string>>;

export const createAppSelector = createSelector.withTypes<RootState>();

export const persistor = persistStore(store);

export default store;
